<template>
	<div>
		<div>
			<div class="flex xs-12 xs-between mt6">
				<div class="fs4 lh125 fw7 c1 mb2">{{ $t("pricing_logic") }}</div>
				<j-button
					v-if="!computedPrice"
					v-can="['writePlans']"
					type="tertiary"
					@click="$modal.show('createPricingLogic')"
				>
					<crm-icon name="add" color="c2" size="small" />
					{{ $t("add") }}
				</j-button>
			</div>
			<j-card class="bg5">
				<template v-if="computedPrice">
					<j-list :list="computedPrice">
						<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
							<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
								{{ item.label }}
							</div>
							<div class="flex xs-6 xs-left xs-middle lh125">
								{{ item.value || "---" }}
							</div>
						</div>
					</j-list>
					<div class="flex xs-12 bt1 bc7 pt2 pb2 xs-right xs-middle">
						<j-button
							type="tertiary"
							size="medium"
							@click="$modal.show('updatePricingLogic')"
						>
							<crm-icon name="edit" color="c6" size="normal" />
						</j-button>
						<j-button type="tertiary" size="medium" @click="handleDelete">
							<crm-icon name="delete" color="c3" size="normal" />
						</j-button>
					</div>
				</template>

				<div class="bg5 ta2 fs3 p2" v-if="!computedPrice">
					{{ $t("errors.no_data") }}
				</div>
			</j-card>
		</div>

		<!-- Create Pricing Logic -->
		<core-modal
			name="createPricingLogic"
			:headline="$t('create_pricing_logic')"
		>
			<pricing-logic-form @submit="onSubmitCreate">
				<div class="xs-12 flex xs-between mt6" slot-scope="{ onSubmit }">
					<j-button @click="onSubmit" :isLoading="isLoading">
						{{ $t("create") }}
					</j-button>
					<j-button
						@click="$modal.hide('createPricingLogic')"
						:isDisabled="isLoading"
						type="bordered"
						>{{ $t("cancel") }}</j-button
					>
				</div>
			</pricing-logic-form>
		</core-modal>

		<!-- Update Pricing Logic -->
		<core-modal
			name="updatePricingLogic"
			:headline="$t('update_pricing_logic')"
		>
			<pricing-logic-form :initialValue="data.price" @submit="onSubmitUpdate">
				<div class="xs-12 flex xs-between mt6" slot-scope="{ onSubmit }">
					<j-button @click="onSubmit" :isLoading="isLoading">
						{{ $t("update") }}
					</j-button>
					<j-button
						@click="$modal.hide('updatePricingLogic')"
						:isDisabled="isLoading"
						type="bordered"
						>{{ $t("cancel") }}</j-button
					>
				</div>
			</pricing-logic-form>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import JDialog from "@/components/JDialog/"

import PricingLogicForm from "./PricingLogicForm"
import api from "../api"

export default {
	props: {
		data: {
			type: Object,
			required: true
		}
	},

	components: {
		JCard,
		JList,
		JButton,
		CrmIcon,
		CoreModal,
		JDialog,
		PricingLogicForm
	},

	data() {
		return {
			isLoading: false
		}
	},

	computed: {
		computedPrice() {
			if (!this.data.price) return null
			return [
				{ label: this.$t("amount"), value: this.data.price.amount },
				{
					label: this.$t("currency_code"),
					value: this.data.price.currencyCode
				},
				{ label: this.$t("amount_logic"), value: this.data.price.amountLogic }
			]
		}
	},

	methods: {
		async onSubmitCreate(payload) {
			this.isLoading = true
			const res = await api.createPricingLogic({
				productId: this.data.id,
				input: { productId: this.data.id, ...payload }
			})

			if (res?.data?.createPlanPriceLogic?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createPlanPriceLogic?.errors ||
							res?.data?.createPlanPriceLogic?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("pricing_logic_created")
					}
				})
			}

			this.$emit("onRefresh")

			this.isLoading = false

			this.$modal.hide("createPricingLogic")
		},
		async onSubmitUpdate(payload) {
			this.isLoading = true
			const res = await api.updatePricingLogic({
				productId: this.data.id,
				input: { productId: this.data.id, ...payload }
			})

			if (res?.data?.updatePlanPriceLogic?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updatePlanPriceLogic?.errors ||
							res?.data?.updatePlanPriceLogic?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("pricing_logic_updated")
					}
				})
			}

			this.$emit("onRefresh")

			this.isLoading = false

			this.$modal.hide("updatePricingLogic")
		},
		async handleDelete() {
			this.isLoading = true
			const res = await api.deletePricingLogic({
				productId: this.data.id
			})

			if (res?.data?.deletePlanPriceLogic?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deletePlanPriceLogic?.errors ||
							res?.data?.deletePlanPriceLogic?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("pricing_logic_deleted")
					}
				})
			}

			this.$emit("onRefresh")

			this.isLoading = false

			this.$modal.hide("j-dialog")
		}
	}
}
</script>
